import React, { useState, useCallback, useRef, useEffect } from "react";
import { registerBlockType } from "@quillforms/blocks";
import PhoneInput, { isPossiblePhoneNumber, isValidPhoneNumber, parsePhoneNumber, Country } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { Label } from "@/components/ui/label";

// Import custom CSS to override PhoneInput styles
import "./phone-input-styles.css";

// Fix for React 18 type compatibility issue with react-phone-number-input
const PhoneInputComponent = PhoneInput as any;

// Explicit BlockProps type for QuillForms custom block
export type BlockProps = {
  id: string;
  next?: () => void;
  attributes: {
    label?: string;
    description?: string;
    required?: boolean;
    placeholder?: string;
    regex?: string;
    [key: string]: any;
  };
  isValid?: boolean;
  isPending?: boolean;
  innerBlocks?: any;
  isReviewing?: boolean;
  isAnswerLocked?: boolean;
  val: string;
  setIsValid: (valid: boolean) => void;
  setIsAnswered: (answered: boolean) => void;
  setIsPending?: (pending: boolean) => void;
  setPendingMsg?: (msg: string | null) => void;
  setValidationErr: (err: string | null) => void;
  setIsAnswerCorrect?: (correct: boolean) => void;
  setVal: (val: string) => void;
  showNextBtn: (show: boolean) => void;
  blockWithError: (err: string) => void;
  showErrMsg: boolean;
  isPreview?: boolean;
  isTouchScreen?: boolean;
  inputRef?: React.RefObject<HTMLInputElement>;
  setFooterDisplay?: (display: boolean) => void;
  formId?: string;
};

registerBlockType("custom-phone-number", {
  supports: {
    editable: true,
  },
  attributes: {
    label: {
      type: "string",
      default: "Phone Number",
    },
    description: {
      type: "string",
      default: "Please enter your phone number",
    },
    required: {
      type: "boolean",
      default: false,
    },
    placeholder: {
      type: "string",
      default: "e.g. (555) 123-4567",
    },
    regex: {
      type: "string",
      default: "^\\+?\\d{10,15}$", // Default: international phone, 10-15 digits
    },
    defaultCountry: {
      type: "string",
      default: "US",
    },
  },

  display: (props: any) => {
    const {
      id,
      attributes,
      setIsValid,
      setIsAnswered,
      setValidationErr,
      showNextBtn,
      val,
      setVal,
      showErrMsg,
      isPreview,
      isTouchScreen,
    } = props;

    const {
      label = "Phone Number",
      description = "Please enter your phone number",
      required = false,
      placeholder = "e.g. (555) 123-4567",
      defaultCountry = (attributes.defaultCountry as Country) || "US",
    } = attributes;

    const [touched, setTouched] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Handle phone number change
    const handleChange = (value: string | undefined) => {
      let phoneNumber = value || "";
      console.log('[PhoneInput] handleChange called with:', value);

      // Attempt to fix copy-pasted national numbers that were interpreted as international
      // e.g. pasting "9787908024" might be interpreted as "+9787908024" (invalid country)
      if (phoneNumber && !isValidPhoneNumber(phoneNumber) && defaultCountry) {
        console.log('[PhoneInput] Number is invalid, attempting to fix...');
        // If the number starts with +, try stripping it and parsing as national
        if (phoneNumber.startsWith('+')) {
           const raw = phoneNumber.substring(1); // remove +
           console.log('[PhoneInput] Stripping + and parsing:', raw);
           const candidate = parsePhoneNumber(raw, defaultCountry);
           console.log('[PhoneInput] Parsed candidate:', candidate);
           if (candidate && isValidPhoneNumber(candidate.number)) {
              console.log('[PhoneInput] Candidate is valid, using:', candidate.number);
              phoneNumber = candidate.number;
           } else {
              console.log('[PhoneInput] Candidate is invalid or missing');
           }
        } else {
           // Also try to parse as national number if no + prefix
           console.log('[PhoneInput] No + prefix, parsing as national:', phoneNumber);
           const candidate = parsePhoneNumber(phoneNumber, defaultCountry);
           console.log('[PhoneInput] Parsed candidate:', candidate);
           if (candidate && isValidPhoneNumber(candidate.number)) {
              console.log('[PhoneInput] Candidate is valid, using:', candidate.number);
              phoneNumber = candidate.number;
           } else {
              console.log('[PhoneInput] Candidate is invalid or missing');
           }
        }
      } else {
        console.log('[PhoneInput] Number is valid or empty, no fix needed');
      }

      console.log('[PhoneInput] Setting value to:', phoneNumber);
      setVal(phoneNumber);
      
      // It's answered if it's more than just the default +1 or country code
      // Max country code length is 3 digits, plus the plus sign = 4 characters
      const isJustCountryCode = phoneNumber.startsWith("+") && phoneNumber.length <= 4;
      setIsAnswered(phoneNumber.length > 0 && !isJustCountryCode);
      
      // Validate phone number
      if (phoneNumber.length > 0) {
        const isValid = isPossiblePhoneNumber(phoneNumber);
        setIsValid(isValid);
        
        if (!isValid && !isJustCountryCode) {
          const errorMsg = "Please enter a valid phone number";
          setValidationErr(errorMsg);
          setError(errorMsg);
        } else {
          setValidationErr(null);
          setError(null);
        }
        showNextBtn(isValid);
      } else if (required) {
        const errorMsg = "This field is required";
        setIsValid(false);
        setValidationErr(errorMsg);
        setError(errorMsg);
        showNextBtn(false);
      } else {
        setIsValid(true);
        setValidationErr(null);
        setError(null);
        showNextBtn(true);
      }
    };

    // Set initial validation state
    useEffect(() => {
      // Set initial validation state when component mounts
      if (val) {
        // If there's a pre-existing value, validate it
        handleChange(val);
      } else {
        // If no value, set to +1 by default if US
        if (defaultCountry === "US") {
          handleChange("+1");
        } else if (required) {
          // If no value and field is required, mark as invalid
          const errorMsg = "This field is required";
          setIsValid(false);
          setValidationErr(errorMsg);
          setError(errorMsg);
          showNextBtn(false);
        } else {
          // If no value and not required, mark as valid
          setIsValid(true);
          setValidationErr(null);
          setError(null);
          showNextBtn(true);
        }
      }
    }, []);

    // Auto-correct invalid international numbers that should be US numbers
    useEffect(() => {
      if (val && !isValidPhoneNumber(val) && defaultCountry === 'US') {
        console.log('[PhoneInput] useEffect detected invalid value:', val);
        if (val.startsWith('+')) {
          const raw = val.substring(1);
          const candidate = parsePhoneNumber(raw, defaultCountry);
          if (candidate && isValidPhoneNumber(candidate.number) && candidate.number !== val) {
            console.log('[PhoneInput] useEffect correcting to:', candidate.number);
            setVal(candidate.number);
          }
        } else {
          const candidate = parsePhoneNumber(val, defaultCountry);
          if (candidate && isValidPhoneNumber(candidate.number) && candidate.number !== val) {
            console.log('[PhoneInput] useEffect correcting to:', candidate.number);
            setVal(candidate.number);
          }
        }
      }
    }, [val, defaultCountry, setVal]);

    return (
      <div className="w-full max-w-lg mx-auto space-y-4">
        <Label className="font-medium text-foreground">{description}</Label>
        <PhoneInputComponent
          className="custom-phone-input"
          international
          withCountryCallingCode
          defaultCountry={defaultCountry}
          value={val}
          onChange={handleChange}
          placeholder={placeholder}
          error={showErrMsg ? error : undefined}
        />
        {error && showErrMsg && (
          <p className="text-sm text-red-500">{error}</p>
        )}
        {description && (!error || !showErrMsg) && (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}
      </div>
    );
  },
});

export default registerBlockType;